// Auto-collision — a blocked-cell map derived from the placed tile dioramas.
// For each diorama we sample a grid over its footprint and cast a ray straight
// DOWN; if the first thing the ray hits sits well above the ground (a building,
// wall, tree, rock, lantern…) that cell is "blocked". Open ground / paths /
// courtyards stay walkable. Characters reject moves into blocked cells, so they
// can't walk through solid objects — only along paths, doorways, and bridges.
//
// Collision falls out of the art automatically: no hand-authored masks. Rebuilt
// whenever the tiles change (clearCollision + re-register).

import * as THREE from 'three'
import { GROUND_Y } from './hexGrid.js'

const CELL = 0.35            // grid resolution (world units)
const OBSTACLE_RISE = 0.18   // hit this far above the ground surface ⇒ obstacle

const blocked = new Set()    // quantized "cx,cz" cell keys
const _ray = new THREE.Raycaster()
const _origin = new THREE.Vector3()
const _down = new THREE.Vector3(0, -1, 0)

function cellKey(x, z) {
  return `${Math.round(x / CELL)},${Math.round(z / CELL)}`
}

// True if a world (x,z) point is inside a blocked cell.
export function isBlocked(x, z) {
  return blocked.has(cellKey(x, z))
}

export function clearCollision() {
  blocked.clear()
}

// Sample a placed diorama (a wrap Group already positioned in the scene) and
// mark its obstacle cells. Call after the diorama is added + positioned.
export function registerDiorama(wrap) {
  for (const c of sampleBlockedCells(wrap)) blocked.add(c)
}

// The blocked cells of one placed diorama, as a fresh Set of keys (see isBlockedIn). registerDiorama adds them to the home field's map;
// a visited kingdom (scene/kingdomView.js) keeps its own set, so the two never mix.
export function sampleBlockedCells(wrap) {
  const out = new Set()
  wrap.updateMatrixWorld(true)
  const meshes = []
  wrap.traverse((o) => { if (o.isMesh) meshes.push(o) })
  if (!meshes.length) return out
  const box = new THREE.Box3().setFromObject(wrap)
  const yTop = box.max.y + 1 // start the ray above everything on this tile
  const threshold = GROUND_Y + OBSTACLE_RISE
  for (let x = box.min.x; x <= box.max.x; x += CELL) {
    for (let z = box.min.z; z <= box.max.z; z += CELL) {
      _ray.set(_origin.set(x, yTop, z), _down)
      const hits = _ray.intersectObjects(meshes, false)
      if (hits.length && hits[0].point.y > threshold) out.add(cellKey(x, z))
    }
  }
  return out
}
export const isBlockedIn = (set, x, z) => set.has(cellKey(x, z))

export function blockedCount() {
  return blocked.size
}
